import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ISelectable } from '@ispm/therapies/interfaces/selectable.interface';
import { TherapiesActionCreatorService } from '@ispm/therapies/services/therapies-action-creator.service';
import { Observable } from 'rxjs/Observable';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { PlaceholderModalComponent } from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import { PlaceholderModalLoaderPayload } from '@ispm/layout/classes/placeholder-modal-loader-payload';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { TherapiesService } from '@ispm/therapies/services/therapies.service';
import { take, switchMap, map, takeUntil } from 'rxjs/operators';
import { combineLatest } from 'rxjs/observable/combineLatest';
import {
  SelectableModalComponent,
  SelectableModalPayload
} from '@ispm/therapies/components/selectable-modal/selectable-modal.component';
import { Subject } from 'rxjs/Subject';

@Component({
  selector: 'gen-treatment-option',
  templateUrl: './treatment-option.component.html',
  styleUrls: ['./treatment-option.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TreatmentOptionComponent implements OnInit, ISelectable {

  @Input()
  data: any;

  @Input()
  indexProperty: string = 'id';

  @Input()
  fields: any[];

  @Input()
  isSelectable: boolean;

  @Input()
  infoFormId: string;

  @Output()
  onSelect: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  onDeselect: EventEmitter<any> = new EventEmitter<any>();

  public isSelected$: Observable<boolean>;
  private _selectableModalPayload: SelectableModalPayload;
  public selectionAllowed$: Observable<boolean>;

  private destroy$ = new Subject();
  private destroyObservable$ = this.destroy$.asObservable();

  constructor(
    private _therapiesAC: TherapiesActionCreatorService,
    private _modalLoader: DynamicModalLoaderService
  ) {
  }

  ngOnInit() {
    this.isSelected$ = this._therapiesAC.getIsSelectedStream(
      TherapiesActionCreatorService.getTherapyIdentifier(this.data, this.indexProperty)
    ).pipe(takeUntil(this.destroyObservable$));

    this.selectionAllowed$ = combineLatest(
      this.isSelected$,
      this._therapiesAC.getIsOverLimitationStream().pipe(map(over => !over)),
    ).pipe(
      takeUntil(this.destroyObservable$),
      map(([isSelected, ...conditions]: Array<boolean>) => {
        return isSelected || conditions.every(condition => !!condition);
      }),
    );

    this._selectableModalPayload = SelectableModalPayload.builder()
      .setOnSelectChangeCallback(this.selectClicked.bind(this))
      .setShowSelectable(this.isSelectable)
      .setFormId(this.infoFormId)
      .setFormData(this.data)
      .setPopupMode(true)
      .setDisabled(true)
      .setEditMode(true);
  }

  ngOnDestroy(){
    this.destroy$.next();
  }

  selectClicked(status: boolean) {
    status
      ? this.onSelect.emit(this.data)
      : this.onDeselect.emit(this.data);
  }

  infoClicked() {
    this.isSelected$
      .pipe(
        take(1),
        switchMap((isSelected) => {
          return this.popupModal(isSelected);
        })
      )
      .subscribe();
  }

  private popupModal(isSelected: boolean) {
    this._selectableModalPayload.setSelectedStatus(isSelected);
    return this._modalLoader.popupModal(PlaceholderModalComponent,
      PlaceholderModalLoaderPayload.builder()
        .setComponentToLoad(SelectableModalComponent)
        .setTitle(this.data.treatmentPlanName)
        .setPayload(this._selectableModalPayload),
      ModalTypes.CUSTOM,
      TherapiesService.ModalLoaderTunnelName
    );
  }
}
